import { useState, useEffect, useContext, useMemo } from 'react';
import { AppContext } from '@/App';
import axios from 'axios';
import { canUserDelete } from '@/utils/permissions';
import { Package, TrendingUp, TrendingDown, Download, RefreshCw, Loader2, AlertCircle, Factory, Trash2, Calendar, Filter, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Legend } from 'recharts';

function DrossStock() {
  const { API, user } = useContext(AppContext);
  const [drossData, setDrossData] = useState([]);
  const [trendData, setTrendData] = useState([]);
  const [totals, setTotals] = useState({
    totalDross: 0,
    totalInput: 0,
    avgDrossPercent: 0,
    furnaceCount: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [message, setMessage] = useState(null);
  
  // Date range filter state
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [filterApplied, setFilterApplied] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'desc' });

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) return <ArrowUpDown size={12} className="ml-1 inline opacity-40" />;
    return sortConfig.direction === 'asc'
      ? <ArrowUp size={12} className="ml-1 inline text-blue-600" />
      : <ArrowDown size={12} className="ml-1 inline text-blue-600" />;
  };

  const sortedDrossData = useMemo(() => {
    if (!sortConfig.key) return drossData;
    return [...drossData].sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];
      if (typeof aVal === 'string') aVal = aVal.toLowerCase();
      if (typeof bVal === 'string') bVal = bVal.toLowerCase();
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [drossData, sortConfig]);
  
  // Check if user can delete (Admin or 'full' access)
  const canDelete = canUserDelete(user, '/raw-material/dross-stock');

  useEffect(() => {
    fetchDrossData();
  }, []);

  const fetchDrossData = async (start = '', end = '') => {
    setLoading(true);
    setError(null);
    try {
      // Build query params for date range
      const params = new URLSearchParams();
      if (start) params.append('start_date', start);
      if (end) params.append('end_date', end);
      const queryString = params.toString() ? `?${params.toString()}` : '';
      
      // Fetch dross stock summary from production data
      const [stockRes, trendRes] = await Promise.all([
        axios.get(`${API}/raw-material/dross-stock${queryString}`),
        axios.get(`${API}/raw-material/dross-stock/trend${queryString}`)
      ]);
      
      setDrossData(stockRes.data.summary || []);
      setTotals(stockRes.data.totals || {
        totalDross: 0,
        totalInput: 0,
        avgDrossPercent: 0,
        furnaceCount: 0
      });
      setTrendData(trendRes.data.trend || []);
      setFilterApplied(!!start || !!end);
    } catch (err) {
      console.error('Error fetching dross data:', err);
      setError('Failed to load dross data from production records');
    } finally {
      setLoading(false);
    }
  };

  const applyDateFilter = () => {
    fetchDrossData(startDate, endDate);
  };

  const clearDateFilter = () => {
    setStartDate('');
    setEndDate('');
    fetchDrossData('', '');
  };

  const deleteOldEntry = async (furnace, date) => {
    if (!window.confirm(`Delete all production entries for ${furnace} from ${date}? This cannot be undone.`)) {
      return;
    }
    
    setDeleting(`${furnace}-${date}`);
    setMessage(null);
    try {
      const response = await axios.delete(
        `${API}/production/entries/by-criteria?furnace=${encodeURIComponent(furnace)}&date=${encodeURIComponent(date)}`
      );
      setMessage({ type: 'success', text: `Deleted ${response.data.deletedCount} entries for ${furnace}` });
      // Refresh data with current filter
      fetchDrossData(startDate, endDate);
    } catch (err) {
      console.error('Error deleting entry:', err);
      setMessage({ type: 'error', text: `Failed to delete: ${err.response?.data?.detail || err.message}` });
    } finally {
      setDeleting(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="animate-spin text-blue-500 mr-2" size={24} />
        <span className="text-gray-600">Loading dross data from production...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dross-stock-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dross Stock</h1>
          <p className="text-gray-500 mt-1">Auto-fetched from Production Report - Monitor dross inventory by furnace</p>
        </div>
        <div className="flex items-center space-x-3">
          <button 
            onClick={() => fetchDrossData(startDate, endDate)}
            className="bg-white border border-gray-200 text-gray-700 px-4 py-2.5 rounded-xl text-sm font-medium flex items-center space-x-2 hover:bg-gray-50"
          >
            <RefreshCw size={18} />
            <span>Refresh</span>
          </button>
          <button className="bg-white border border-gray-200 text-gray-700 px-4 py-2.5 rounded-xl text-sm font-medium flex items-center space-x-2 hover:bg-gray-50">
            <Download size={18} />
            <span>Export</span>
          </button>
        </div>
      </div>

      {/* Date Range Filter */}
      <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <Calendar className="text-gray-500" size={18} />
            <span className="text-sm font-medium text-gray-700">Date Range:</span>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Start Date"
            />
            <span className="text-gray-400">to</span>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="End Date"
            />
          </div>
          <button
            onClick={applyDateFilter}
            disabled={!startDate && !endDate}
            className="px-4 py-2 bg-[#1a237e] text-white rounded-lg text-sm font-medium hover:bg-[#283593] disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <Filter size={16} />
            Apply Filter
          </button>
          {filterApplied && (
            <button
              onClick={clearDateFilter}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 flex items-center gap-2"
            >
              Clear Filter
            </button>
          )}
          {filterApplied && (
            <span className="text-sm text-blue-600 bg-blue-50 px-3 py-1 rounded-full">
              Showing: {startDate || 'Start'} → {endDate || 'End'}
            </span>
          )}
        </div>
      </div>

      {error && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center gap-3">
          <AlertCircle className="text-amber-600" size={20} />
          <div>
            <p className="text-amber-800 font-medium">{error}</p>
            <p className="text-amber-600 text-sm">Make sure production entries have dross data recorded.</p>
          </div>
        </div>
      )}

      {message && (
        <div className={`${message.type === 'success' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'} border rounded-xl p-4`}>
          <p className={message.type === 'success' ? 'text-green-800' : 'text-red-800'}>{message.text}</p>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-[#1a237e]/5 to-[#283593]/10 rounded-xl p-5 border border-[#1a237e]/20">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-[#1a237e] font-medium">Total Dross Generated</p>
              <p className="text-2xl font-bold text-[#1a237e]">{totals.totalDross.toLocaleString()} kg</p>
              <p className="text-xs text-gray-500 mt-1">From {totals.furnaceCount} furnace(s)</p>
            </div>
            <div className="p-3 bg-[#1a237e] rounded-xl">
              <Package className="text-white" size={24} />
            </div>
          </div>
        </div>
        
        <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl p-5 border border-orange-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-orange-600 font-medium">Avg Dross %</p>
              <p className="text-2xl font-bold text-orange-700">{totals.avgDrossPercent.toFixed(1)}%</p>
              <p className="text-xs text-gray-500 mt-1">Of total input material</p>
            </div>
            <div className="p-3 bg-orange-500 rounded-xl">
              <TrendingDown className="text-white" size={24} />
            </div>
          </div>
        </div>
        
        <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-5 border border-green-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-green-600 font-medium">Material Recovery</p>
              <p className="text-2xl font-bold text-green-700">{(100 - totals.avgDrossPercent).toFixed(1)}%</p>
              <p className="text-xs text-gray-500 mt-1">Output vs Input ratio</p>
            </div>
            <div className="p-3 bg-green-500 rounded-xl">
              <TrendingUp className="text-white" size={24} />
            </div>
          </div>
        </div>
      </div>

      {/* Trend Chart */}
      {trendData.length > 0 && (
        <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Dross Generation Trend (from Production)</h3>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={trendData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} unit=" kg" />
              <Tooltip 
                contentStyle={{ borderRadius: '12px', border: '1px solid #e5e7eb' }} 
                formatter={(value) => [`${value.toFixed(0)} kg`, 'Dross']}
              />
              <Bar dataKey="total" fill="#1a237e" radius={[4, 4, 0, 0]} name="Total Dross" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Dross by Furnace Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
        <div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
          <h3 className="font-semibold text-gray-900">Dross by Furnace (from Production Entries)</h3>
        </div>
        <div className="overflow-x-auto">
          {drossData.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Package className="mx-auto mb-3 text-gray-300" size={48} />
              <p className="font-medium">No dross data found</p>
              <p className="text-sm">Dross will appear here once recorded in Production Data Entry</p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-gradient-to-r from-gray-50 to-slate-50">
                <tr>
                  <th onClick={() => handleSort('source')} className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase cursor-pointer hover:bg-gray-100 select-none">Furnace <SortIcon columnKey="source" /></th>
                  <th onClick={() => handleSort('quantity')} className="px-4 py-3.5 text-right text-xs font-semibold text-gray-600 uppercase cursor-pointer hover:bg-gray-100 select-none">Total Dross <SortIcon columnKey="quantity" /></th>
                  <th onClick={() => handleSort('totalInput')} className="px-4 py-3.5 text-right text-xs font-semibold text-gray-600 uppercase cursor-pointer hover:bg-gray-100 select-none">Total Input <SortIcon columnKey="totalInput" /></th>
                  <th onClick={() => handleSort('totalOutput')} className="px-4 py-3.5 text-right text-xs font-semibold text-green-600 uppercase cursor-pointer hover:bg-gray-100 select-none">Total Output <SortIcon columnKey="totalOutput" /></th>
                  <th onClick={() => handleSort('drossPercent')} className="px-4 py-3.5 text-center text-xs font-semibold text-gray-600 uppercase cursor-pointer hover:bg-gray-100 select-none">Dross % <SortIcon columnKey="drossPercent" /></th>
                  <th onClick={() => handleSort('entries')} className="px-4 py-3.5 text-center text-xs font-semibold text-gray-600 uppercase cursor-pointer hover:bg-gray-100 select-none">Entries <SortIcon columnKey="entries" /></th>
                  <th onClick={() => handleSort('lastUpdated')} className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase cursor-pointer hover:bg-gray-100 select-none">Last Updated <SortIcon columnKey="lastUpdated" /></th>
                  <th className="px-4 py-3.5 text-center text-xs font-semibold text-gray-600 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {sortedDrossData.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-2">
                        <div className="p-1.5 bg-blue-100 rounded">
                          <Factory className="text-blue-600" size={16} />
                        </div>
                        <span className="font-medium text-gray-900">{item.source}</span>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-right">
                      <span className="font-semibold text-orange-600">{item.quantity.toLocaleString()} kg</span>
                    </td>
                    <td className="px-4 py-4 text-right text-sm text-gray-600">
                      {item.totalInput.toLocaleString()} kg
                    </td>
                    <td className="px-4 py-4 text-right">
                      <span className="font-semibold text-green-600">{(item.totalOutput || 0).toLocaleString()} kg</span>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center justify-center space-x-2">
                        <div className="w-20 bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-orange-500 h-2 rounded-full" 
                            style={{ width: `${Math.min(item.drossPercent * 5, 100)}%` }}
                          ></div>
                        </div>
                        <span className="text-sm font-medium text-gray-700">{item.drossPercent}%</span>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-center">
                      <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium">
                        {item.entries} entries
                      </span>
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-500">{item.lastUpdated}</td>
                    <td className="px-4 py-4 text-center">
                      {canDelete && item.lastUpdated && item.lastUpdated < '2026-01-01' && (
                        <button
                          onClick={() => deleteOldEntry(item.source, item.lastUpdated)}
                          disabled={deleting === `${item.source}-${item.lastUpdated}`}
                          className="p-1.5 text-red-500 hover:bg-red-50 rounded disabled:opacity-50"
                          title={`Delete old entries for ${item.source}`}
                        >
                          {deleting === `${item.source}-${item.lastUpdated}` ? (
                            <Loader2 size={16} className="animate-spin" />
                          ) : (
                            <Trash2 size={16} />
                          )}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-gray-50 border-t border-gray-200">
                <tr>
                  <td className="px-4 py-3 font-semibold text-gray-900">Total</td>
                  <td className="px-4 py-3 text-right font-bold text-orange-600">{totals.totalDross.toLocaleString()} kg</td>
                  <td className="px-4 py-3 text-right font-medium text-gray-700">{totals.totalInput.toLocaleString()} kg</td>
                  <td className="px-4 py-3 text-center font-medium text-gray-700">{totals.avgDrossPercent.toFixed(1)}%</td>
                  <td className="px-4 py-3 text-center text-gray-500">-</td>
                  <td className="px-4 py-3"></td>
                  <td className="px-4 py-3"></td>
                </tr>
              </tfoot>
            </table>
          )}
        </div>
      </div>

      {/* Info Note */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <p className="text-blue-800 text-sm">
          <strong>Note:</strong> Dross data is automatically calculated from Production Data Entry records. 
          Each production entry records dross generated during the melting process.
        </p>
      </div>
    </div>
  );
}

export default DrossStock;
